<template>
  <div class="status-bar">
    <div class="status-content">
      <div class="status-item">
        <i class="pi pi-desktop mr-1"></i>
        <span class="status-label">Agent:</span>
        <span v-if="selectedAgent" class="status-value clickable" @click="openSettingsDialog">
          {{ selectedAgent.name }}
          <Tag
            :value="selectedAgent.state"
            :severity="getAgentStatusSeverity(selectedAgent.state)"
            class="ml-1"
          />
        </span>
        <span v-else class="status-value text-500 clickable" @click="openSettingsDialog">
          Not Selected
        </span>
      </div>

      <div class="status-divider"></div>

      <div class="status-item">
        <i class="pi pi-cog mr-1"></i>
        <span class="status-label">Scale Desk:</span>
        <span v-if="selectedScaleDesk" class="status-value">
          {{ selectedScaleDesk.name }}
          <Tag :value="selectedScaleDesk.location || 'Unknown'" severity="secondary" class="ml-1" />
        </span>
        <span v-else class="status-value text-500">Not Selected</span>
      </div>
    </div>
  </div>

  <!-- Settings Dialog -->
  <Dialog
    v-model:visible="showSettingsDialog"
    header="Agent & Scale Desk Settings"
    :modal="true"
    :style="{ width: '80vw', maxWidth: '800px' }"
    :maximizable="true"
  >
    <DeviceSettings />
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Tag from 'primevue/tag'
import Dialog from 'primevue/dialog'
import { useLocalSettingsStore } from '@/stores/localSettingsStore'
import { useDevicesStore } from '@/stores/v2/devices'
import DeviceSettings from '@/views/settings/DeviceSettings.vue'
import type { AgentState } from '@/types/Agent'

const localSettings = useLocalSettingsStore()

// Reactive state - get the reactive ref directly from the devices store
const devicesStore = useDevicesStore()
const agents = computed(() => devicesStore.agents)
const showSettingsDialog = ref(false)

// Computed properties
const selectedAgent = computed(() => {
  if (!localSettings.settings.selectedAgentId) return null
  return agents.value.find(
    (agent: AgentState) => agent.id === localSettings.settings.selectedAgentId,
  )
})

const allScaleDesks = computed(() => {
  const desks: Array<any & { agentId: string; agentName: string }> = []
  agents.value.forEach((agent) => {
    const list = [
      ...(((agent.devices && agent.devices.scales) as any[]) || []),
      ...(((agent.devices && agent.devices.cameras) as any[]) || []),
      ...(((agent.devices && agent.devices.scanners) as any[]) || []),
    ]
    list.forEach((device: any) => {
      desks.push({ ...device, agentId: agent.id, agentName: agent.name })
    })
  })
  return desks
})

const selectedScaleDesk = computed(() => {
  if (!localSettings.settings.selectedScaleDeskId) return null
  // Prefer matching within the selected agent
  const withinSelectedAgent = selectedAgent.value
    ? [
        ...(((selectedAgent.value.devices && selectedAgent.value.devices.scales) as any[]) || []),
        ...(((selectedAgent.value.devices && selectedAgent.value.devices.cameras) as any[]) || []),
        ...(((selectedAgent.value.devices && selectedAgent.value.devices.scanners) as any[]) || []),
      ].find((d: any) => d.id === localSettings.settings.selectedScaleDeskId)
    : null
  if (withinSelectedAgent) return withinSelectedAgent as unknown as any
  return allScaleDesks.value.find(
    (desk) => desk.id === localSettings.settings.selectedScaleDeskId,
  ) as unknown as any
})

// Helper function
function getAgentStatusSeverity(state: string) {
  switch (state) {
    case 'online':
      return 'success'
    case 'offline':
      return 'danger'
    default:
      return 'secondary'
  }
}

// Click handler
function openSettingsDialog() {
  showSettingsDialog.value = true
}

// Note: useLiveDocument handles lifecycle management automatically
</script>

<style scoped>
.status-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--surface-50);
  border-top: 1px solid var(--surface-200);
  padding: 0.25rem 1rem;
  z-index: 1000;
  font-size: 0.75rem;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.status-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.status-label {
  font-weight: 500;
  color: var(--text-color-secondary);
}

.status-value {
  font-weight: 600;
  color: var(--text-color);
}

.clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}

.clickable:hover {
  opacity: 0.8;
}

.status-divider {
  width: 1px;
  height: 1rem;
  background: var(--surface-300);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .status-content {
    flex-direction: column;
    gap: 0.5rem;
  }

  .status-divider {
    display: none;
  }

  .status-bar {
    padding: 0.5rem 1rem;
  }
}

/* Ensure content doesn't get hidden behind status bar */
:global(.layout-content) {
  padding-bottom: 3rem;
}

@media (max-width: 768px) {
  :global(.layout-content) {
    padding-bottom: 4rem;
  }
}
</style>
